import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import usePunchPointStore from '../store/punchPointStore';
import useProjectStore from '../store/projectStore';
import useUIStore from '../store/uiStore';
import { getEmployees, getDepartments } from '../services/employeeService';
import { getIssueTypes, createIssueType } from '../services/punchPointService';
import { isFieldAllowed } from '../constants/fieldPermissions';
import useAuthStore from '../store/authStore';

const initialForm = {
  entry_type: 'issue',
  project_id: '',
  punch_point_type: 'site_issue',
  category: '',
  issue_description: '',
  severity: 'medium',
  assignment_type: 'department', // 'individual' | 'department'
  assigned_to_supervisor_id: '',
  assigned_to_dept_id: '',
  expected_completion_date: '',
};

const CreatePunchPointPage = () => {
  const [form, setForm] = useState(initialForm);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [issueTypes, setIssueTypes] = useState([]);
  const [newTypeName, setNewTypeName] = useState('');
  const [addingType, setAddingType] = useState(false);
  const [fieldPermissions, setFieldPermissions] = useState(null);
  const { projects, fetchProjects } = useProjectStore();
  const { createPunchPoint, isSubmitting } = usePunchPointStore();
  const { user } = useAuthStore();
  const showToast = useUIStore((s) => s.showToast);
  const navigate = useNavigate();

  const loadIssueTypes = () => getIssueTypes().then((res) => setIssueTypes(res.data.issue_types || [])).catch(() => {});

  useEffect(() => {
    fetchProjects();
    getEmployees().then((res) => setEmployees(res.data.employees || [])).catch(() => {});
    loadIssueTypes();
    getDepartments()
      .then((res) => {
        const depts = res.data.departments || [];
        setDepartments(depts);
        if (user?.department_id) {
          const dept = depts.find((d) => d.id === user.department_id);
          setFieldPermissions(dept?.field_permissions || null);
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddIssueType = async () => {
    const name = newTypeName.trim();
    if (!name) return;
    setAddingType(true);
    try {
      await createIssueType(name);
      setNewTypeName('');
      await loadIssueTypes();
      setForm((f) => ({ ...f, punch_point_type: name }));
      showToast('Issue type added', 'success');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to add issue type', 'error');
    }
    setAddingType(false);
  };

  const showField = (key) => isFieldAllowed(fieldPermissions, 'punch_point', key);

  const handleChange = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.project_id || !form.issue_description || !form.expected_completion_date) {
      showToast('Please fill in all required fields', 'error');
      return;
    }

    const payload = {
      project_id: Number(form.project_id),
      entry_type: form.entry_type,
      punch_point_type: form.punch_point_type,
      category: form.category || null,
      issue_description: form.issue_description,
      severity: form.severity,
      expected_completion_date: form.expected_completion_date,
      assigned_to_supervisor_id:
        form.assignment_type === 'individual' && form.assigned_to_supervisor_id
          ? Number(form.assigned_to_supervisor_id)
          : null,
      assigned_to_dept_id:
        form.assignment_type === 'department' && form.assigned_to_dept_id
          ? Number(form.assigned_to_dept_id)
          : null,
    };

    const result = await createPunchPoint(payload);
    if (result.success) {
      showToast('Punch point created', 'success');
      navigate('/punch-points');
    } else {
      showToast(result.error, 'error');
    }
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-2xl mx-auto glass-card p-6">
        <h1 className="text-xl font-bold text-gray-900 mb-6">New Task / Issue Entry</h1>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Entry Type *</label>
            <div className="flex gap-4 text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  checked={form.entry_type === 'task'}
                  onChange={() => setForm((f) => ({ ...f, entry_type: 'task' }))}
                />
                Task
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  checked={form.entry_type === 'issue'}
                  onChange={() => setForm((f) => ({ ...f, entry_type: 'issue' }))}
                />
                Issue
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Project *</label>
            <select
              required
              value={form.project_id}
              onChange={handleChange('project_id')}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            >
              <option value="">Select a project</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>

          {showField('punch_point_type') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Issue Type *</label>
              <select
                value={form.punch_point_type}
                onChange={handleChange('punch_point_type')}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              >
                {issueTypes.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
              <div className="flex gap-2 mt-2">
                <input
                  placeholder="Add a new type..."
                  value={newTypeName}
                  onChange={(e) => setNewTypeName(e.target.value)}
                  className="flex-1 border border-gray-300 rounded px-2 py-1.5 text-xs"
                />
                <button
                  type="button"
                  disabled={addingType}
                  onClick={handleAddIssueType}
                  className="px-3 py-1.5 text-xs rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                >
                  + Add
                </button>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
            <textarea
              required
              rows={3}
              value={form.issue_description}
              onChange={handleChange('issue_description')}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              placeholder="Describe the issue..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            {showField('severity') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Severity *</label>
                <select
                  value={form.severity}
                  onChange={handleChange('severity')}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Expected Completion *</label>
              <input
                type="date"
                required
                value={form.expected_completion_date}
                onChange={handleChange('expected_completion_date')}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              />
            </div>
          </div>

          {showField('assignment') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Assign To *</label>
              <div className="flex gap-4 mb-3 text-sm">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={form.assignment_type === 'department'}
                    onChange={() => setForm((f) => ({ ...f, assignment_type: 'department' }))}
                  />
                  Department
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={form.assignment_type === 'individual'}
                    onChange={() => setForm((f) => ({ ...f, assignment_type: 'individual' }))}
                  />
                  Individual
                </label>
              </div>

              {form.assignment_type === 'department' ? (
                <select
                  value={form.assigned_to_dept_id}
                  onChange={handleChange('assigned_to_dept_id')}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                >
                  <option value="">Select a department</option>
                  {departments.map((d) => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              ) : (
                <select
                  value={form.assigned_to_supervisor_id}
                  onChange={handleChange('assigned_to_supervisor_id')}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                >
                  <option value="">Select a person</option>
                  {employees.map((e) => (
                    <option key={e.id} value={e.id}>{e.name} — {e.role}</option>
                  ))}
                </select>
              )}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => navigate('/punch-points')}
              className="px-4 py-2 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold disabled:opacity-60"
            >
              {isSubmitting ? 'Creating...' : 'Create Entry'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreatePunchPointPage;
